<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header__icon"><i class="fa fa-chart-bar"></i></div>
        <div class="panel-header__text">
          <div class="panel-header__title">Location Wise Report</div>
          <div class="panel-header__sub">Service transactions grouped by location and service type</div>
        </div>
      </div>

      <!-- Sticky filter toolbar -->
      <div class="filter-bar">

        <!-- Row 1: text search -->
        <div class="filter-bar__row filter-bar__row--search">
          <div class="search-inline">
            <div class="search-box">
              <i class="fa fa-search search-box__icon"></i>
              <input type="text" class="search-box__input" placeholder="Search by enrollment no, TCN or name…"
                v-model="search_text" @keypress.enter="searchByTCN" />
            </div>
            <button class="search-btn" @click="searchByTCN">
              <i class="fa fa-search"></i> Search
            </button>
          </div>
        </div>

        <!-- Row 2: AdvSearch filters (renders its own Bootstrap row grid) -->
        <div class="filter-bar__row filter-bar__row--adv">
          <adv-search @search="advSearch" :is_date_range="true" :is_service="true" :is_status="false"
            :is_location_enrollment="true" :is_location="currentUser.u.is_service_provider" :reportData="excelreports"
            :titleworksheet="titleworksheet" :is_default_tramist_status="'200'" :json_fields="json_fields"
            :header="header" :report_name="'service_report.xls'" :export_inline="true" />
        </div>

      </div>

      <!-- Loading -->
      <div class="state-box" v-if="getLoadingData">
        <div class="state-spinner"></div>
        <span>Loading report data…</span>
      </div>

      <!-- Prompt: select criteria -->
      <div class="state-box state-box--muted" v-else-if="load">
        <i class="fa fa-filter state-box__icon"></i>
        <div class="state-box__title">Select search criteria</div>
        <div class="state-box__sub">Use the filters above to generate a report</div>
      </div>

      <!-- Empty -->
      <div class="state-box state-box--muted" v-else-if="!load && reports.length === 0">
        <i class="fa fa-inbox state-box__icon"></i>
        <div class="state-box__title">No records found</div>
        <div class="state-box__sub">Try adjusting your filters</div>
      </div>

      <!-- Report body -->
      <div class="report-body" v-if="reports.length > 0 && !getLoadingData">

        <div v-for="(report, i) in reports" :key="i">
          <div v-for="(loc, j) in report.locations" :key="j" class="location-block">

            <!-- Location header -->
            <div class="location-header">
              <i class="fa fa-map-marker-alt"></i>
              {{ loc.location_name }}
            </div>

            <div v-for="(svc, k) in loc.services" :key="k" class="service-block">

              <!-- Service sub-header -->
              <div class="service-header">
                <i class="fa fa-fingerprint"></i>
                {{ svc.service_name }}
              </div>

              <!-- Data table -->
              <div class="table-wrap">
                <table class="report-table">
                  <thead>
                    <tr>
                      <th class="th-sm">#</th>
                      <th class="th-name">Applicant</th>
                      <th class="th-ori" v-if="svc.service_type === 'fdle'">ORI</th>
                      <th class="th-status">Status</th>
                      <th class="th-date">Enrolled</th>
                      <th class="th-num">Qty</th>
                      <th class="th-num">Cost</th>
                      <th class="th-num" v-if="currentUser.u.is_service_provider">Profit</th>
                      <th class="th-num" v-if="currentUser.u.is_service_provider">Tax</th>
                      <th class="th-num">Commission</th>
                      <th class="th-num">Selling Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, idx) in getTableListArray(svc.data)" :key="idx"
                      :class="{ 'row--sub': item.parent_id }">
                      <td class="td-sm">{{ item.sn || '' }}</td>
                      <td>
                        <template v-if="!item.parent_id">
                          <div class="applicant-name">{{ item.applicant }}</div>
                          <div class="applicant-tcn" v-if="item.tcn">{{ item.tcn }}</div>
                        </template>
                        <template v-else>
                          <span class="sub-indent">↳ <em>{{ item.additional_title }}</em></span>
                        </template>
                      </td>
                      <td v-if="svc.service_type === 'fdle'">{{ item.title }}</td>
                      <td>
                        <span class="status-chip" :class="statusClass(item.service_status)">
                          {{ item.service_status }}
                        </span>
                      </td>
                      <td>{{ item.enroll_date || '—' }}</td>
                      <td class="td-num">{{ item.qty || '' }}</td>
                      <td class="td-num">{{ item.cost ? '$ ' + $options.filters.toFixedTwoDigit(item.cost) : '$ 0.00' }}
                      </td>
                      <td class="td-num" v-if="currentUser.u.is_service_provider">
                        {{ item.markup ? '$ ' + $options.filters.toFixedTwoDigit(item.markup) : '$ 0.00' }}
                      </td>
                      <td class="td-num" v-if="currentUser.u.is_service_provider">
                        {{ item.tax ? '$ ' + $options.filters.toFixedTwoDigit(item.tax) : '$ 0.00' }}
                      </td>
                      <td class="td-num">{{ item.commission ? '$ ' + $options.filters.toFixedTwoDigit(item.commission) :
                        '$ 0.00' }}</td>
                      <td class="td-num">{{ item.selling_price ? '$ ' +
                        $options.filters.toFixedTwoDigit(item.selling_price) : '$ 0.00' }}
                      </td>
                    </tr>

                    <!-- Sub-total row -->
                    <tr class="subtotal-row">
                      <td :colspan="svc.service_type === 'fdle' ? 5 : 4" class="subtotal-label">
                        Sub Total
                      </td>
                      <td class="td-num subtotal-val"></td>
                      <td class="td-num subtotal-val">
                        $ {{getTableListArray(svc.data).reduce((a, o) => a + Number(o.cost), 0) | toFixedTwoDigit}}
                      </td>
                      <td class="td-num subtotal-val" v-if="currentUser.u.is_service_provider">
                        $ {{getTableListArray(svc.data).reduce((a, o) => a + Number(o.markup), 0) | toFixedTwoDigit}}
                      </td>
                      <td class="td-num subtotal-val" v-if="currentUser.u.is_service_provider">
                        $ {{getTableListArray(svc.data).reduce((a, o) => a + Number(o.tax), 0) | toFixedTwoDigit}}
                      </td>
                      <td class="td-num subtotal-val">
                        $ {{getTableListArray(svc.data).reduce((a, o) => a + Number(o.commission), 0) | toFixedTwoDigit
                        }}
                      </td>
                      <td class="td-num subtotal-val">
                        $ {{getTableListArray(svc.data).reduce((a, o) => a + Number(o.selling_price), 0) |
                        toFixedTwoDigit }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

            </div>
          </div>
        </div>

        <!-- Grand total bar -->
        <div class="grand-total">
          <div class="grand-total__label">Grand Total</div>
          <div class="grand-total__amounts">
            <div class="grand-total__item">
              <span class="grand-total__key">Cost</span>
              <span class="grand-total__val">
                $ {{ grandTotals.cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                }}
              </span>
            </div>
            <template v-if="currentUser.u.is_service_provider">
              <div class="grand-total__item">
                <span class="grand-total__key">Markup</span>
                <span class="grand-total__val">
                  $ {{ grandTotals.markup.toLocaleString(undefined, {
                    minimumFractionDigits: 2, maximumFractionDigits: 2
                  }) }}
                </span>
              </div>
              <div class="grand-total__item">
                <span class="grand-total__key">Tax</span>
                <span class="grand-total__val">
                  $ {{ grandTotals.tax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  }}
                </span>
              </div>
            </template>
            <div class="grand-total__item">
              <span class="grand-total__key">Commission</span>
              <span class="grand-total__val">
                $ {{ grandTotals.commission.toLocaleString(undefined, {
                  minimumFractionDigits: 2, maximumFractionDigits:
                2 }) }}
              </span>
            </div>
            <div class="grand-total__item grand-total__item--highlight">
              <span class="grand-total__key">Selling Price</span>
              <span class="grand-total__val">
                $ {{ grandTotals.selling_price.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                maximumFractionDigits: 2 })
                }}
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import {
  FETCH_CLIENTS,
  FETCH_SERVICES,
  FETCH_LOCATION_WISE_REPORT,
} from "@/core/services/store/actions.type";
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import { mapGetters } from "vuex";
import AdvSearch from "@/includes/search/AdvSearch";

export default {
  components: { AdvSearch },
  computed: {
    ...mapGetters(["currentUser", "getClients", "getServices", "getCompanies", "getSearchDate"]),
    grandTotals() {
      const sum = (field) =>
        this.reports.reduce(
          (acc, r) =>
            acc + r.locations.reduce(
              (a2, loc) =>
                a2 + loc.services.reduce(
                  (a3, svc) =>
                    a3 + this.getTableListArray(svc.data).reduce(
                      (a4, e) => a4 + parseFloat(e[field] || 0), 0
                    ), 0
                ), 0
            ), 0
        );
      return {
        cost: sum("cost"),
        markup: sum("markup"),
        tax: sum("tax"),
        commission: sum("commission"),
        selling_price: sum("selling_price"),
      };
    },
  },
  filters: {
    toFixedTwoDigit(amount) {
      return parseFloat(amount).toFixed(2);
    },
  },
  data() {
    return {
      search_text: "",
      search: {
        service: "all",
        company: "all",
        date_from: "",
        date_to: "",
        service_status: "200",
        payment_status: "all",
      },
      getLoadingData: false,
      isTableVisible: true,
      reports: [],
      header: [],
      load: true,
      excelreports: [],
      titleworksheet: "",
      json_fields: {
        "": "applicant",
        " ": "ori",
        "  ": "service_status",
        "   ": "enroll_date",
        "    ": "cost",
        "     ": "markup",
        "      ": "tax",
        "       ": "commission",
        "        ": "selling_price",
      },
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Report", route: "" },
        { id: 3, title: "Location Report", route: "" },
      ],
    };
  },
  mounted() {
    this.$store.dispatch(FETCH_CLIENTS);
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.$store.dispatch(FETCH_SERVICES, this.currentUser.u.is_client ? "self" : "all");
  },
  methods: {
    statusClass(status) {
      if (!status) return "";
      const s = status.toLowerCase();
      if (s.includes("transmit")) return "chip--blue";
      if (s.includes("final")) return "chip--green";
      if (s.includes("draft")) return "chip--grey";
      return "chip--grey";
    },
    getTableListArray(data) {
      const result = [];
      let index = 1;
      data.forEach((item) => {
        result.push({ ...item, isAdditional: false, sn: index++ });
        if (item.additional_service && item.additional_service.length > 0) {
          item.additional_service.forEach((sub) => {
            result.push({ ...sub, parent_id: item.id, isAdditional: true });
          });
        }
      });
      return result;
    },
    searchByTCN() {
      if (this.search_text) {
        this.searchRequestHandler({ search_text: this.search_text });
      } else {
        this.$toastr.e("Please enter a TCN or name to search");
      }
    },
    advSearch(value) {
      this.search_text = "";
      const { header, search, footer } = value;
      this.header = header;
      this.search = { ...search };
      search.search_text = "";
      this.tablereset();
      this.titleworksheet = search.date_from + " - " + search.date_to;
      search.payment_status = "all";
      this.searchRequestHandler(search, footer);
    },
    tablereset() {
      // column layout is now driven by v-if in template — no resets needed
    },
    searchRequestHandler(search, footer = null) {
      this.getLoadingData = true;
      this.load = false;
      this.$store
        .dispatch(FETCH_LOCATION_WISE_REPORT, search)
        .then((res) => {
          this.getLoadingData = false;
          this.isTableVisible = true;
          this.reports = res.data;

          const tempArray = [];
          res.data.forEach((element) => {
            element.locations.forEach((element1) => {
              element1.services.forEach((element2) => {
                const totalCost = element2.data.reduce((a, o) => a + Number(o.cost), 0);
                const totalMarkup = element2.data.reduce((a, o) => a + Number(o.markup), 0);
                const totalTax = element2.data.reduce((a, o) => a + Number(o.tax), 0);
                const totalCommission = element2.data.reduce((a, o) => a + Number(o.commission), 0);
                const totalSellingPrice = element2.data.reduce((a, o) => a + Number(o.selling_price), 0);

                const header = `<td colspan=7>${element.sp_name}\n${element1.location_name}\n${element2.service_name}</th>`;
                tempArray.push(
                  { applicant: header, ori: "", service_status: "", enroll_date: "", cost: "", markup: "", tax: "", commission: "", selling_price: "" },
                  { applicant: "Applicant Name", tcn: "TCN", ori: "ORI", service_status: "Status", enroll_date: "Enrolled Date", cost: "Cost", markup: "Markup", tax: "Tax", commission: "Commission", selling_price: "Selling Price" },
                  ...element2.data.map((e3) => {
                    e3.enroll_date = new Date(e3.enroll_date).toLocaleDateString();
                    const s = e3.service_status;
                    if ((s === "200" || s === "500") && e3.service_type !== "fingerprintcard") e3.service_status = "Ready To Transmit";
                    else if ((s === "200" || s === "500") && e3.service_type === "fingerprintcard") e3.service_status = "Final";
                    else if ((s === "300" || s === "600") && e3.service_type === "fingerprintcard") e3.service_status = "Final";
                    else if ((s === "300" || s === "600") && e3.service_type !== "fingerprintcard") e3.service_status = "Transmitted";
                    else if (s === "100" || s === "400") e3.service_status = "Draft";
                    else e3.service_status = "-";
                    if (element2.service_type !== "fdle") element1.ori = "";
                    return e3;
                  }),
                  { applicant: "Sub Total", ori: "", service_status: "", enroll_date: "", cost: totalCost, markup: totalMarkup, tax: totalTax, commission: totalCommission, selling_price: totalSellingPrice },
                  { applicant: "", ori: "", service_status: "", enroll_date: "", cost: "", markup: "", tax: "", commission: "", selling_price: "" }
                );
              });
            });
            this.excelreports = [...tempArray];
          });

          if (res.data.length > 0) {
            const gt = this.grandTotals;
            this.excelreports.push({
              applicant: "Grand Total", ori: "", service_status: "", enroll_date: "",
              cost: gt.cost, markup: gt.markup, tax: gt.tax,
              commission: gt.commission, selling_price: gt.selling_price,
            });
          }

          if (footer) {
            this.excelreports.push({ applicant: "", ori: "", service_status: "", enroll_date: "", selling_price: footer[3], tax: footer[2], markup: footer[1], cost: footer[0] });
          }
        })
        .catch((err) => {
          this.getLoadingData = false;
          this.msg = err.error;
        });
    },
    statusColor(status) {
      const map = { P: "warning", C: "success", U: "info", Cancel: "danger" };
      return map[status] || "";
    },
  },
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;
$green: #276749;
$border: #dde2ec;

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.page-wrap {
  padding: 12px;
  font-family: $font;
}

/* ── panel card ── */
.panel-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: clip;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

/* ── header ── */
.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid $border;
  background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, $blue, #2277aa);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
    flex-shrink: 0;
  }

  &__text {
    flex: 1;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: $dark;
    line-height: 1.2;
  }

  &__sub {
    font-size: 12px;
    color: #7a8499;
    margin-top: 3px;
  }
}

/* ── filter bar ── */
.filter-bar {
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid $border;
  background: #fafbfd;
  position: sticky;
  top: 0;
  z-index: 20;

  &__row {
    width: 100%;

    &--search {
      padding: 12px 16px 0;
    }

    &--adv {
      padding: 4px 16px 12px;
    }
  }
}

.search-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.search-box {
  display: inline-flex;
  align-items: center;
  border: 1.5px solid $border;
  border-radius: 9px;
  overflow: hidden;
  background: #fff;
  width: 340px;

  &__icon {
    padding: 0 10px;
    color: #a0aab8;
    font-size: 13px;
    flex-shrink: 0;
  }

  &__input {
    flex: 1;
    height: 36px;
    border: none;
    outline: none;
    font-size: 13px;
    font-family: $font;
    color: $dark;

    &::placeholder {
      color: #b0b8cc;
    }
  }
}

.search-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  background: $blue;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: $font;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, box-shadow 0.15s;

  &:hover {
    background: #2d85bb;
    box-shadow: 0 4px 12px rgba(56, 154, 212, 0.3);
  }
}

/* ── state boxes ── */
.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 24px;
  gap: 8px;
  font-family: $font;

  &--muted .state-box__icon {
    color: #c5d0e0;
  }

  &__icon {
    font-size: 36px;
    margin-bottom: 4px;
  }

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: $dark;
  }

  &__sub {
    font-size: 13px;
    color: #7a8499;
  }
}

.state-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #c5d8f5;
  border-top-color: $blue;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin-bottom: 8px;
}

/* ── report body — scrollable container ── */
.report-body {
  overflow-x: auto;
  overflow-y: auto;
  max-height: calc(100vh - 260px);
  padding-bottom: 24px;
}

/* ── location block ── */
.location-block {
  margin-bottom: 8px;
}

.location-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  font-size: 13px;
  font-weight: 700;
  color: $dark;
  background: #eef4fb;
  border-top: 1px solid $border;
  border-bottom: 1px solid $border;
  position: sticky;
  top: 0;
  z-index: 10;

  i {
    color: $blue;
    font-size: 12px;
  }
}

/* ── service block ── */
.service-block {
  margin-bottom: 4px;
}

.service-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 24px 8px 36px;
  font-size: 12px;
  font-weight: 600;
  color: #5a6278;
  background: #f8faff;
  border-bottom: 1px solid $border;
  position: sticky;
  top: 41px;
  z-index: 9;

  i {
    color: #a0aab8;
    font-size: 11px;
  }
}

/* ── table ── */
.table-wrap {
  padding: 0 24px;
  min-width: 900px;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
  color: $dark;
  table-layout: fixed;

  th {
    padding: 9px 12px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #7a8499;
    border-bottom: 2px solid $border;
    white-space: nowrap;
    background: #fafbfd;
    position: sticky;
    top: 73px;
    z-index: 8;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .th-sm {
    width: 44px;
    text-align: center;
  }

  .th-num {
    width: 90px;
    text-align: right;
  }

  .th-name {
    width: 22%;
  }

  .th-status {
    width: 110px;
  }

  .th-date {
    width: 110px;
  }

  .th-ori {
    width: 80px;
  }

  td {
    padding: 9px 12px;
    border-bottom: 1px solid #f0f2f8;
    vertical-align: middle;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .td-sm {
    text-align: center;
    color: #a0aab8;
    font-size: 11px;
  }

  .td-num {
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  tr:hover td {
    background: #f8fbff;
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr.row--sub td {
    background: #fdfeff;
    color: #7a8499;
  }
}

/* ── applicant cell ── */
.applicant-name {
  font-size: 12.5px;
}

.applicant-tcn {
  font-size: 10.5px;
  color: #a0aab8;
  margin-top: 1px;
}

.sub-indent {
  font-size: 12px;
  color: #7a8499;
}

/* ── status chip ── */
.status-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: #f0f3fa;
  color: #7a8499;

  &.chip--blue {
    background: rgba(56, 154, 212, 0.12);
    color: $blue;
  }

  &.chip--green {
    background: rgba(39, 103, 73, 0.1);
    color: $green;
  }

  &.chip--grey {
    background: #f0f3fa;
    color: #7a8499;
  }
}

/* ── subtotal row ── */
.subtotal-row td {
  background: #f4f6fb !important;
  border-top: 2px solid $border;
  border-bottom: 2px solid $border;
}

.subtotal-label {
  text-align: right;
  font-size: 12px;
  font-weight: 700;
  color: $dark;
  padding-right: 16px !important;
}

.subtotal-val {
  font-size: 12.5px;
  font-weight: 700;
  color: $dark;
  font-variant-numeric: tabular-nums;
}

/* ── grand total ── */
.grand-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin: 20px 24px 0;
  padding: 18px 24px;
  background: #1a2340;
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(13, 27, 62, 0.2);

  &__label {
    font-size: 14px;
    font-weight: 700;
    color: #fff;
    white-space: nowrap;
  }

  &__amounts {
    display: flex;
    align-items: center;
    gap: 24px;
    flex-wrap: wrap;
  }

  &__item {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;

    &--highlight .grand-total__val {
      color: #5dd6ff;
      font-size: 15px;
    }
  }

  &__key {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: rgba(255, 255, 255, 0.45);
  }

  &__val {
    font-size: 13.5px;
    font-weight: 700;
    color: #fff;
    font-variant-numeric: tabular-nums;
  }
}
</style>
